import type { ReactElement, ReactNode } from "react";
import { DevelopersArchitectureEmbed } from "./developers-architecture-embed";
import type {
  FunnelIntroYoutube,
  FunnelSection,
} from "./developers-funnel-content";
import { FunnelIcon } from "./developers-funnel-icons";
import { FunnelImage } from "./developers-funnel-image";
import { YouTubeLogo } from "./developers-funnel-social";
import { DevelopersGrowthSplitSection } from "./developers-growth-split-section";
import { DevelopersStackLogos } from "./developers-stack-logos";
import { DevelopersWorkSection } from "./developers-work-section";

/** Turns `*phrase*` in copy into italic. */
function renderMarkedIntro(text: string): ReactNode {
  const parts: readonly string[] = text.split(/(\*[^*]+\*)/g);
  let cursor: number;
  cursor = 0;
  return parts.map((part: string): ReactNode => {
    const key: string = `intro-${cursor}`;
    cursor += part.length;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

function renderIntro(
  text: string,
  youtube: FunnelIntroYoutube | undefined,
): ReactNode {
  if (youtube === undefined) return renderMarkedIntro(text);
  const splitAt: number = text.indexOf(youtube.word);
  if (splitAt < 0) return renderMarkedIntro(text);
  const before: string = text.slice(0, splitAt);
  const after: string = text.slice(splitAt + youtube.word.length);
  return (
    <>
      {renderMarkedIntro(before)}
      <a
        className="dvf-intro-yt"
        href={youtube.href}
        target="_blank"
        rel="noreferrer"
      >
        <YouTubeLogo />
        <span>{youtube.word}</span>
      </a>
      {renderMarkedIntro(after)}
    </>
  );
}

function sectionMedia(section: FunnelSection): ReactElement | null {
  if (section.embed) {
    return (
      <DevelopersArchitectureEmbed
        slot={section.embed}
        className="dvf-section-media"
      />
    );
  }
  if (section.image) {
    return (
      <FunnelImage
        slot={section.image}
        className="dvf-section-media"
        sizes="(min-width: 768px) 50vw, 100vw"
      />
    );
  }
  return null;
}

/**
 * One content band: image or embed, heading, intro, then bullets with icons.
 * Stacked on phone; two columns from tablet up. Every other section flips.
 */
export function DevelopersFunnelSection({
  section,
  flip,
}: {
  readonly section: FunnelSection;
  readonly flip: boolean;
}): ReactElement {
  if (section.media === "product-mockup") {
    return <DevelopersWorkSection section={section} />;
  }
  if (section.media === "growth-chart") {
    return <DevelopersGrowthSplitSection section={section} />;
  }

  const media: ReactElement | null = sectionMedia(section);

  const sectionClassName: string = [
    "dvf-section",
    flip ? "dvf-section-alt" : "",
    section.embed ? "dvf-section-embed" : "",
  ]
    .filter((part: string) => part.length > 0)
    .join(" ");

  return (
    <section id={section.id} className={sectionClassName}>
      <div className="dvf-section-inner">
        <div className="dvf-section-grid">
          {section.id === "stack" && media !== null ? (
            <div className="dvf-stack-media">
              {media}
              <DevelopersStackLogos className="dvf-logos-note-media" />
            </div>
          ) : (
            media
          )}
          <div>
            <h2 className="dvf-section-title">{section.heading}</h2>
            {section.intro ? (
              <p className="dvf-section-intro">
                {renderIntro(section.intro, section.introYoutube)}
              </p>
            ) : null}

            <ul className="dvf-bullets">
              {section.bullets.map((bullet) => (
                <li className="dvf-bullet" key={bullet.title}>
                  <span className="dvf-bullet-icon">
                    <FunnelIcon
                      name={bullet.icon}
                      size={section.id === "offer" ? 16 : 20}
                    />
                  </span>
                  <div>
                    <p className="dvf-bullet-title">{bullet.title}</p>
                    <p className="dvf-bullet-text">{bullet.text}</p>
                  </div>
                </li>
              ))}
            </ul>
            {section.id === "stack" ? (
              <DevelopersStackLogos className="dvf-logos-note-end" />
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
